import { Injectable } from '@angular/core';
import { EncryptionService } from './encryption.service';

/**
 * Secure Storage Service
 * 
 * Provides a drop-in replacement for localStorage with automatic encryption
 * for sensitive authentication and user data.
 */
@Injectable({
  providedIn: 'root'
})
export class SecureStorageService {
  private migrationCompleted = false;

  constructor(private readonly encryptionService: EncryptionService) {
    // Note: Async initialization will be handled when service is first used
  }

  /**
   * Initialize the service and perform migration if needed
   */
  private async initializeService(): Promise<void> {
    if (!this.encryptionService.isAvailable()) {
      console.warn('Encryption not available, falling back to unencrypted storage');
      return;
    }

    try {
      await this.encryptionService.migrateExistingData();
      this.migrationCompleted = true;
    } catch (error) {
      console.error('Migration failed:', error);
    }
  }

  /**
   * Ensures migration is completed before operations
   */
  private async ensureMigration(): Promise<void> {
    if (!this.migrationCompleted && this.encryptionService.isAvailable()) {
      await this.initializeService();
    }
  }

  /**
   * Stores data securely with encryption for sensitive keys
   */
  async setItem(key: string, value: string): Promise<void> {
    // Ensure migration is completed before storing
    await this.ensureMigration();
    
    if (!this.encryptionService.isAvailable()) {
      localStorage.setItem(key, value);
      return;
    }

    try {
      await this.encryptionService.setItem(key, value);
    } catch (error) {
      console.error(`Failed to store ${key} securely:`, error);
      // Fallback to regular storage for non-sensitive data
      localStorage.setItem(key, value);
    }
  }

  /**
   * Retrieves data and decrypts if necessary
   */
  async getItem(key: string): Promise<string | null> {
    // Ensure migration is completed before retrieving
    await this.ensureMigration();
    
    if (!this.encryptionService.isAvailable()) {
      return localStorage.getItem(key);
    }

    try {
      return await this.encryptionService.getItem(key);
    } catch (error) {
      console.error(`Failed to retrieve ${key} securely:`, error);
      // Fallback to regular storage
      return localStorage.getItem(key);
    }
  }

  /**
   * Stores JSON data securely
   */
  async setObject(key: string, value: any): Promise<void> {
    const jsonString = JSON.stringify(value);
    await this.setItem(key, jsonString);
  }

  /**
   * Retrieves and parses JSON data
   */
  async getObject<T = any>(key: string): Promise<T | null> {
    const jsonString = await this.getItem(key);
    if (!jsonString) {
      return null;
    }

    try {
      return JSON.parse(jsonString) as T;
    } catch (error) {
      console.error(`Failed to parse JSON for ${key}:`, error);
      return null;
    }
  }

  /**
   * Removes an item from storage
   */
  removeItem(key: string): void {
    if (this.encryptionService.isAvailable()) {
      this.encryptionService.removeItem(key);
    } else {
      localStorage.removeItem(key);
    }
  }

  /**
   * Clears all sensitive data
   */
  clearSensitiveData(): void {
    if (this.encryptionService.isAvailable()) {
      this.encryptionService.clearSensitiveData();
    } else {
      // Manual cleanup for fallback
      const sensitiveKeys = [
        'token', 'refresh_token', 'user', 'permissions', 
        'role', 'school', 'campuses', 'preferences', 'fcm_token'
      ];
      for (const key of sensitiveKeys) {
        localStorage.removeItem(key);
      }
    }
  }

  /**
   * Synchronous getter for backward compatibility
   * Note: This will only work for data that's already been migrated
   */
  getItemSync(key: string): string | null {
    console.warn(`Synchronous access to ${key} - consider using async getItem() instead`);
    return localStorage.getItem(key);
  }

  /**
   * Synchronous setter for backward compatibility
   */
  setItemSync(key: string, value: string): void {
    console.warn(`Synchronous storage of ${key} - consider using async setItem() instead`);
    localStorage.setItem(key, value);
  }

  /**
   * Check if migration is complete
   */
  isMigrationCompleted(): boolean {
    return this.migrationCompleted;
  }

  /**
   * Force migration of existing data
   */
  async forceMigration(): Promise<void> {
    if (this.encryptionService.isAvailable()) {
      await this.encryptionService.migrateExistingData();
      this.migrationCompleted = true;
    }
  }
}